import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { invoicesAPI, customersAPI, productsAPI, companyAPI } from '../../services/api';
import toast from 'react-hot-toast';
import PrintButton, { printNode } from '../../utils/print';
import InvoiceDocument from '../common/InvoiceDocument';
import ProductPicker from '../common/ProductPicker';
import BillToEditor, { EMPTY_BILL_TO, billToFromCustomer, billToPatch, billToChanged } from './BillToEditor';
import { getDisplayCurrency, CURRENCY_REGISTRY, convertAmount, rateLabel } from '../../utils/currency';
import '../../styles/invoice-editor.css';

const fmt = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format;

export default function InvoiceForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [currencies, setCurrencies] = useState(CURRENCY_REGISTRY);
  const [rates, setRates] = useState([]);
  const [baseCurrency, setBaseCurrency] = useState(getDisplayCurrency());
  const [form, setForm] = useState({
    customer_id: '', invoice_date: new Date().toISOString().split('T')[0],
    due_date: new Date(Date.now() + 30*86400000).toISOString().split('T')[0],
    currency: getDisplayCurrency(), exchange_rate: 1,
    description: '', memo: '', items: [{ product_id: '', description: '', quantity: 1, unit_price: 0, tax_rate: 0, amount: 0 }]
  });
  const [loading, setLoading] = useState(false);
  const [created, setCreated] = useState(null);
  const [billTo, setBillTo] = useState({ ...EMPTY_BILL_TO });
  const [savedBillTo, setSavedBillTo] = useState(null);
  const formRef = useRef(null);
  const docRef = useRef(null);
  const createdDocRef = useRef(null);

  useEffect(() => {
    customersAPI.list({}).then(setCustomers).catch(console.error);
    productsAPI.list({}).then(setProducts).catch(console.error);
    companyAPI.currencies().then((cs) => { if (Array.isArray(cs) && cs.length) setCurrencies(cs); }).catch(() => {});
    companyAPI.exchangeRates().then((rs) => setRates(Array.isArray(rs) ? rs : [])).catch(() => {});
    companyAPI.current().then((c) => { if (c?.base_currency) setBaseCurrency(c.base_currency); }).catch(() => {});
    if (id) invoicesAPI.get(id).then(d => {
      setForm({ ...d, currency: d.currency || getDisplayCurrency(), exchange_rate: d.exchange_rate || 1, items: d.items || [] });
      // GET /invoices/:id only joins the customer name, so pull the full
      // record to seed the editable Bill To block.
      if (!d.customer_id) return;
      customersAPI.get(d.customer_id).then(c => {
        setCustomers(prev => (prev.some(x => x.id === c.id) ? prev : [...prev, c]));
        const bt = billToFromCustomer(c);
        setBillTo(bt);
        setSavedBillTo(bt);
      }).catch(() => {
        const bt = billToFromCustomer({ name: d.customer_name });
        setBillTo(bt);
        setSavedBillTo(bt);
      });
    }).catch(e => toast.error(e.message));
  }, [id]);

  const docCurrency = (form.currency && /^[A-Z]{3}$/.test(String(form.currency))) ? form.currency : (baseCurrency || 'USD');
  const fxRate = parseFloat(form.exchange_rate);
  const effectiveRate = isNaN(fxRate) || fxRate <= 0 ? 1 : fxRate;

  // Auto-fill the exchange rate when the invoice currency is not the base currency.
  useEffect(() => {
    if (docCurrency === baseCurrency) {
      if (form.exchange_rate && parseFloat(form.exchange_rate) !== 1) {
        setForm(f => ({ ...f, exchange_rate: 1 }));
      }
      return;
    }
    const direct = rates.find(r => r.from_currency === docCurrency && r.to_currency === baseCurrency);
    const reverse = rates.find(r => r.from_currency === baseCurrency && r.to_currency === docCurrency);
    const auto = direct ? parseFloat(direct.rate) : (reverse && parseFloat(reverse.rate) > 0) ? 1 / parseFloat(reverse.rate) : null;
    const cur = parseFloat(form.exchange_rate);
    // exchange_rate starts at 1 (placeholder) — treat as unset so auto-fill applies.
    if (auto != null && auto > 0 && (!form.exchange_rate || cur === 1)) {
      setForm(f => ({ ...f, exchange_rate: auto }));
    }
  }, [docCurrency, baseCurrency, rates]);

  const updateItem = (i, field, val) => {
    setForm(prev => {
      const items = [...prev.items];
      items[i] = { ...items[i], [field]: val };
      const amt = (items[i].quantity || 0) * (items[i].unit_price || 0);
      const disc = amt * ((items[i].discount_percent || 0) / 100);
      items[i].amount = amt - disc;
      return { ...prev, items };
    });
  };

  const addItem = () => setForm({ ...form, items: [...form.items, { product_id: '', description: '', quantity: 1, unit_price: 0, tax_rate: 0, amount: 0 }] });
  const removeItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });

  const selectProduct = (i, productId) => {
    const p = products.find(pr => pr.id === parseInt(productId));
    if (p) {
      updateItem(i, 'product_id', p.id);
      updateItem(i, 'unit_price', p.sales_price);
      updateItem(i, 'description', p.description || p.name);
      updateItem(i, 'tax_rate', p.tax_rate);
    }
  };


  const subtotal = form.items.reduce((s, item) => s + (item.amount || 0), 0);
  const taxTotal = form.items.reduce((s, item) => s + ((item.amount || 0) * (item.tax_rate || 0) / 100), 0);
  const total = subtotal + taxTotal;

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    // Bill To edits belong on the customer record, not just this invoice.
    // A failure here (e.g. missing edit permission) must not block the save.
    if (form.customer_id && billToChanged(savedBillTo, billTo)) {
      try {
        await persistBillTo(billToPatch(billTo));
      } catch (err) {
        toast.error(`Invoice saved, but the customer details were not updated: ${err.message}`);
      }
    }
    try {
      const data = { ...form, total, tax_amount: taxTotal, subtotal };
      if (id) { await invoicesAPI.update(id, data); toast.success('Invoice updated'); navigate('/invoices'); }
      else {
        const res = await invoicesAPI.create(data);
        toast.success('Invoice created');
        // Fetch the saved invoice (server generates the number) so the
        // print dialog can offer printing the real document.
        const saved = await invoicesAPI.get(res.id);
        setCreated(saved);
      }
    } catch (err) { toast.error(err.message); }
    setLoading(false);
  };

  const handlePrintCreated = () => {
    if (created && createdDocRef.current) {
      printNode(createdDocRef.current, `Invoice ${created.invoice_number}`);
    }
    navigate('/invoices');
  };

  const createdDocData = created ? {
    invoice_number: created.invoice_number,
    invoice_date: created.invoice_date,
    due_date: created.due_date,
    payment_terms: created.payment_terms,
    status: created.status,
    customer_name: created.customer_name,
    customer_email: created.customer_email,
    customer_phone: created.customer_phone,
    customer_address: created.cust_addr1,
    currency: created.currency,
    exchange_rate: created.exchange_rate,
    items: created.items,
    subtotal: created.subtotal,
    tax_amount: created.tax_amount,
    total: created.total,
    amount_paid: created.amount_paid,
    amount_due: created.amount_due,
    memo: created.memo || created.notes,
  } : null;

  const selectedCustomer = customers.find(c => c.id === parseInt(form.customer_id));

  // Bill To edits are written back to the customer record.
  const persistBillTo = async (values) => {
    const customerId = form.customer_id;
    if (!customerId) throw new Error('Select a customer first');
    if (!String(values.name || '').trim()) throw new Error('Bill To name is required');
    const res = await customersAPI.update(customerId, values);
    // The API appends a `message` field; keep it out of the customer record.
    const { message, ...updated } = res;
    setCustomers(prev => prev.map(c => (c.id === parseInt(customerId) ? { ...c, ...updated } : c)));
    setSavedBillTo(billToFromCustomer({ ...(selectedCustomer || {}), ...updated }));
    toast.success('Customer information updated');
    return updated;
  };

  const onCustomerChange = (customerId) => {
    setForm({ ...form, customer_id: customerId });
    const c = customers.find(x => x.id === parseInt(customerId));
    const bt = billToFromCustomer(c);
    setBillTo(bt);
    setSavedBillTo(bt);
  };

  const docData = {
    invoice_number: form.invoice_number,
    invoice_date: form.invoice_date,
    due_date: form.due_date,
    payment_terms: form.payment_terms,
    status: form.status || 'Draft',
    currency: docCurrency,
    exchange_rate: effectiveRate,
    customer_name: billTo.name || (selectedCustomer && selectedCustomer.name),
    customer_email: billTo.email || (selectedCustomer && selectedCustomer.email) || '',
    customer_phone: billTo.phone || (selectedCustomer && selectedCustomer.phone) || '',
    customer_address: [
      billTo.address_line1,
      billTo.address_line2,
      [billTo.city, billTo.state, billTo.postal_code].filter(Boolean).join(', '),
      billTo.country,
    ].filter(Boolean).join(', '),
    items: form.items.map(item => ({ ...item, tax_amount: (item.amount || 0) * (item.tax_rate || 0) / 100 })),
    subtotal,
    tax_amount: taxTotal,
    total,
  };

  return (
    <div>
      <div className="toolbar"><h2>{id ? 'Edit Invoice' : 'New Invoice'}</h2></div>
      <form onSubmit={handleSubmit} ref={formRef}>
        <div className="card" style={{marginBottom:16}}>
          <div className="card-body">
            <div className="form-grid">
              <div className="form-group"><label>Customer <span className="required">*</span></label>
                <select className="form-control" required value={form.customer_id || ''} onChange={e => onCustomerChange(e.target.value)}>
                  <option value="">Select customer...</option>
                  {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="form-group"><label>Invoice Date <span className="required">*</span></label><input className="form-control" type="date" required value={form.invoice_date} onChange={e => setForm({...form, invoice_date: e.target.value})} /></div>
              <div className="form-group"><label>Due Date <span className="required">*</span></label><input className="form-control" type="date" required value={form.due_date} onChange={e => setForm({...form, due_date: e.target.value})} /></div>
              <div className="form-group"><label>Currency</label>
                <select className="form-control" value={form.currency || ''} onChange={e => setForm({...form, currency: e.target.value})}>
                  {currencies.map(c => <option key={c.code} value={c.code}>{c.code}</option>)}
                </select>
              </div>
              {docCurrency !== baseCurrency && (
                <div className="form-group"><label>Exchange Rate</label>
                  <div className="form-control" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 0 }}>
                    <input type="number" step="0.0001" min="0" style={{ flex: 1, border: 'none', outline: 'none', padding: '8px 10px' }} value={form.exchange_rate}
                      onChange={e => setForm({...form, exchange_rate: e.target.value})} />
                    <span className="text-muted" style={{ paddingRight: 10, whiteSpace: 'nowrap' }}>{rateLabel(docCurrency, effectiveRate, baseCurrency)}</span>
                  </div>
                </div>
              )}
              <div className="form-group"><label>Description</label><input className="form-control" value={form.description || ''} onChange={e => setForm({...form, description: e.target.value})} /></div>
              <div className="form-group full-width"><label>Bill To</label>
                <BillToEditor
                  billTo={billTo}
                  onChange={setBillTo}
                  customerId={form.customer_id || null}
                  customerName={selectedCustomer?.name}
                  onSaveToCustomer={persistBillTo}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="card" style={{marginBottom:16}}>
          <div className="card-header"><h3>Line Items</h3></div>
          <div className="card-body" style={{padding:0}}>
            <table>
              <thead><tr><th style={{width:200}}>Product</th><th>Description</th><th style={{width:80}}>Qty</th><th style={{width:120}}>Price</th><th style={{width:80}}>Tax %</th><th style={{width:120}} className="text-right">Amount</th><th style={{width:40}}></th></tr></thead>
              <tbody>
                {form.items.map((item, i) => (
                  <tr key={i}>
                    <td>
                      <ProductPicker
                        value={item.product_id || ''}
                        products={products}
                        onSelect={(p) => selectProduct(i, p.id)}
                        onCreated={async (newId) => {
                          const list = await productsAPI.list({});
                          setProducts(list);
                          const p = list.find(x => x.id === newId);
                          if (p) {
                            updateItem(i, 'product_id', p.id);
                            updateItem(i, 'unit_price', p.sales_price);
                            updateItem(i, 'description', p.description || p.name);
                            updateItem(i, 'tax_rate', p.tax_rate);
                          }
                        }}
                      />
                    </td>
                    <td><input className="form-control" value={item.description || ''} onChange={e => updateItem(i, 'description', e.target.value)} /></td>
                    <td><input className="form-control" type="number" step="0.01" min="0" value={item.quantity} onChange={e => updateItem(i, 'quantity', parseFloat(e.target.value) || 0)} /></td>
                    <td><input className="form-control" type="number" step="0.01" min="0" value={item.unit_price} onChange={e => updateItem(i, 'unit_price', parseFloat(e.target.value) || 0)} /></td>
                    <td><input className="form-control" type="number" step="0.01" min="0" value={item.tax_rate || 0} onChange={e => updateItem(i, 'tax_rate', parseFloat(e.target.value) || 0)} /></td>
                    <td className="amount">{fmt(item.amount)}</td>
                    <td><button type="button" className="btn-ghost btn-sm" onClick={() => removeItem(i)}>✕</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="card-body" style={{borderTop:'1px solid #e2e8f0'}}>
            <button type="button" className="btn btn-outline btn-sm" onClick={addItem}>+ Add Line</button>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <div style={{display:'flex',justifyContent:'flex-end',gap:40,padding:'8px 16px',alignItems:'center'}}>
              <div><span className="text-muted">Subtotal:</span> <strong>{fmt(subtotal)} {docCurrency}</strong></div>
              <div><span className="text-muted">Tax:</span> <strong>{fmt(taxTotal)} {docCurrency}</strong></div>
              <div style={{fontSize:18}}><span className="text-muted">Total:</span> <strong>{fmt(total)} {docCurrency}</strong></div>
              {docCurrency !== baseCurrency && total > 0 && (
                <div style={{fontSize:14, color:'#2563eb'}}>
                  <span className="text-muted">≈ </span><strong>{fmt(convertAmount(total, effectiveRate))} {baseCurrency}</strong>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="form-actions">
          <PrintButton targetRef={docRef} title={`Invoice ${id || 'Draft'} - ${new Date().toLocaleDateString()}`} />
          <button type="button" className="btn btn-outline" onClick={() => navigate('/invoices')}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Saving...' : (id ? 'Update' : 'Create')} Invoice</button>
        </div>
      </form>
      <div className="print-only-doc" ref={docRef}>
        <InvoiceDocument data={docData} docType="invoice" />
      </div>

      {/* Hidden document for the print-after-create flow (real server data) */}
      <div className="print-only-doc" ref={createdDocRef}>
        {createdDocData && <InvoiceDocument data={createdDocData} docType="invoice" />}
      </div>

      {created && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 440 }}>
            <div className="modal-header">
              <h3>Invoice created</h3>
              <button type="button" className="btn-ghost" onClick={() => navigate('/invoices')}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ margin: 0, fontSize: 14 }}>
                Invoice <strong>{created.invoice_number}</strong> has been saved.
                Would you like to <strong>print</strong> it now, or just save it?
              </p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline" onClick={() => navigate('/invoices')}>Just Save</button>
              <button type="button" className="btn btn-primary" onClick={handlePrintCreated}>Print Invoice</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
